import { removeCRs } from './mol_format_utils';
import { formResidueKey } from './pdb';

/**
 * @description A class for representing mmCif data.
 * Currently, this only parses the atom_site loop, collecting the atoms into residues.
 */
export class CifData {
    constructor(input) {
        this.residues = [];
        if (typeof input === 'string') {
            const { residues, parseErrors } = CifData.parseCifString(input);
            if (parseErrors?.length > 0) {
                throw new Error(`CIF parsing error: ${parseErrors.join('\n')}`);
            }
            this.residues = residues;
        }
    }

    get atoms() {
        return this.residues.flatMap((residue) => residue.atoms);
    }

    get hasH() {
        return this.residues.some((residue) => residue.hasH);
    }

    /**
     * @param {{
     * hetflag?: boolean, isWater?: boolean, isSingle?: boolean, hasH?: boolean
     * }} filterProps
     */
    filterResidues(filterProps) {
        function check(residue) {
            return Object.entries(filterProps).every(([key, value]) => (
                typeof value === 'function' ? value(residue) : residue[key] === value
            ));
        }
        return this.residues.filter(check);
    }

    getResidue(getProps) {
        return this.filterResidues(getProps)[0];
    }

    static parseCifString(cifString) {
        const loops = removeCRs(cifString).split(/^loop_$/m);

        const atomLoop = loops.find((loop) => loop.match(/^_atom_site\./m));
        if (!atomLoop) {
            return { parseErrors: ['No atom_site loop found'] };
        }

        const { results: atomLines, parseErrors } = cifGetLoop('_atom_site', atomLoop);
        const rawAtoms = atomLines.map((line) => new CifAtomRaw(line));
        const residues = collectResiduesFromCifAtoms(rawAtoms);
        return { residues, parseErrors };
    }
}

/**
 * @description A class for representing residues from mmCif data.
 * resName, seqNum, and chainId return the author version if available, or the label version.
 * resKey is pulled from RawAtom, returning the author version if available, or the label version.
 */
class CifResidue {
    constructor(cifAtomRaw) {
        this.hetflag = cifAtomRaw.atomType === 'HETATM';
        this.labelResName = cifAtomRaw.labelResName;
        this.labelChainId = cifAtomRaw.labelChainId;
        this.labelEntityId = cifAtomRaw.labelEntityId;
        this.labelSeqNum = cifAtomRaw.labelSeqNum;
        this.iCode = cifAtomRaw.iCode;
        this.authSeqNum = cifAtomRaw.authSeqNum;
        this.authResName = cifAtomRaw.authResName;
        this.authChainId = cifAtomRaw.authChainId;
        this.modelNum = cifAtomRaw.modelNum;
        this.labelResKey = cifAtomRaw.labelResKey;
        this.authResKey = cifAtomRaw.authResKey;
        this.atoms = [new CifAtom(cifAtomRaw, this)];
        /** @type {Map<string, CifAtom[]>} */
        this.altLocMap = new Map();
    }

    get resKey() { return this.authResKey || this.labelResKey; }
    get resName() { return this.authResName || this.labelResName; }
    get seqNum() { return this.authSeqNum || this.labelSeqNum; }
    get chainId() { return this.authChainId || this.labelChainId; }
    get hasH() { return this.atoms.some((atom) => atom.isH); }
    get isWater() { return this.resName === 'HOH'; }
    get isSingle() { return this.atoms.length === 1; }

    toString() {
        let key = this.resKey;
        if (this.altLocMap.size > 1) {
            const altlocStr = [...this.altLocMap.entries()].map(([altLoc, list]) => `${altLoc || "''"}:${list.length}`).join(',');
            key += ` (${altlocStr})`;
        }

        return key;
    }

    addAtom(cifAtomRaw) {
        const atom = new CifAtom(cifAtomRaw, this);
        this.atoms.push(atom);
        if (!this.altLocMap.has(atom.altLoc)) {
            this.altLocMap.set(atom.altLoc, []);
        }
        this.altLocMap.get(atom.altLoc).push(atom);
    }
}

/**
 * @description A class for representing atoms from mmCif data.
 * `atomName` returns the author version if available, or the label version.
 */
class CifAtom {
    constructor(inputAtom, residue) {
        this.residue = residue;
        const cifAtomRaw = inputAtom instanceof CifAtomRaw ? inputAtom : new CifAtomRaw(inputAtom);
        this.atomNum = cifAtomRaw.atomNum;
        this.labelAtomName = cifAtomRaw.labelAtomName;
        this.labelAltLoc = cifAtomRaw.labelAltLoc;
        this.x = cifAtomRaw.x;
        this.y = cifAtomRaw.y;
        this.z = cifAtomRaw.z;
        this.occupancy = cifAtomRaw.occupancy;
        this.tempFactor = cifAtomRaw.tempFactor;
        this.element = cifAtomRaw.element;
        this.charge = cifAtomRaw.charge;
        this.authAtomName = cifAtomRaw.authAtomName;
    }

    get hetflag() { return this.residue.hetflag; }
    get labelResName() { return this.residue.labelResName; }
    get labelChainId() { return this.residue.labelChainId; }
    get labelEntityId() { return this.residue.labelEntityId; }
    get labelSeqNum() { return this.residue.labelSeqNum; }
    get iCode() { return this.residue.iCode; }
    get authSeqNum() { return this.residue.authSeqNum; }
    get authResName() { return this.residue.authResName; }
    get authChainId() { return this.residue.authChainId; }
    get modelNum() { return this.residue.modelNum; }
    get labelResKey() { return this.residue.labelResKey; }
    get authResKey() { return this.residue.authResKey; }
    get resKey() { return this.residue.resKey; }
    get atomName() { return this.authAtomName || this.labelAtomName; }
    get isH() { return this.element === 'H'; }

    toString() {
        return `${this.atomNum} ${this.resKey} ${this.atomName}`;
    }
}

/**
 * @description A class for converting cif data objects into atoms, but not kept in CifData.
 * It deals with lower-level CIF stuff: atom_site field names, and `.` and `?` for missing values.
 * It is likely possible to unify this with CifAtom.
 */
export class CifAtomRaw {
    /* eslint-disable */
    // cif
    // HETATM 4548 O  O      . 4K6 C 3 .   ? -17.267 -38.873 -12.728 1.00 51.65  ? 301 4K6 A O      1
    // pdb
    // HETATM 4550  O   4K6 A 301     -17.267 -38.873 -12.728  1.00 51.65           O
    /* eslint-enable */

    /*
        _atom_site.group_PDB
        _atom_site.id
        _atom_site.type_symbol
        _atom_site.label_atom_id
        _atom_site.label_alt_id
        _atom_site.label_comp_id
        _atom_site.label_asym_id
        _atom_site.label_entity_id
        _atom_site.label_seq_id
        _atom_site.pdbx_PDB_ins_code
        _atom_site.Cartn_x
        _atom_site.Cartn_y
        _atom_site.Cartn_z
        _atom_site.occupancy
        _atom_site.B_iso_or_equiv
        _atom_site.pdbx_formal_charge
        _atom_site.auth_seq_id
        _atom_site.auth_comp_id
        _atom_site.auth_asym_id
        _atom_site.auth_atom_id
        _atom_site.pdbx_PDB_model_num
    */

    constructor(fields) {
        this.atomType = fields['group_PDB'];
        this.atomNum = fields['id'];
        this.element = fields['type_symbol'];
        this.labelAtomName = fields['label_atom_id'];
        this.labelAltLoc = fields['label_alt_id'];
        this.labelResName = fields['label_comp_id']; // residue name
        this.labelChainId = fields['label_asym_id']; // chain, ligands on separate chains
        this.labelEntityId = fields['label_entity_id']; // entity (cif specific)
        this.labelSeqNum = fields['label_seq_id']; // residue sequence, but not used for ligands
        this.iCode = fields['pdbx_PDB_ins_code'];
        this.x = fields['Cartn_x'];
        this.y = fields['Cartn_y'];
        this.z = fields['Cartn_z'];
        this.occupancy = fields['occupancy'];
        this.tempFactor = fields['B_iso_or_equiv'];
        this.charge = fields['pdbx_formal_charge'];
        this.authSeqNum = fields['auth_seq_id'];
        this.authResName = fields['auth_comp_id'];
        this.authChainId = fields['auth_asym_id'];
        this.authAtomName = fields['auth_atom_id'];
        this.modelNum = fields['pdbx_PDB_model_num'];

        // CIF uses '?' (missing) and '.' (not applicable) for missing values
        if (this.iCode === '?' || this.iCode === '.') this.iCode = '';
        if (this.labelAltLoc === '?' || this.labelAltLoc === '.') this.labelAltLoc = '';

        this.labelResKey = formResidueKey(
            this.labelResName, this.labelSeqNum, this.labelChainId, { iCode: this.iCode }
        );
        this.authResKey = formResidueKey(
            this.authResName, this.authSeqNum, this.authChainId, { iCode: this.iCode }
        );
    }

    get resKey() { return this.authResKey || this.labelResKey; }
    get resName() { return this.authResName || this.labelResName; }
    get seqNum() { return this.authSeqNum || this.labelSeqNum; }
    get chainId() { return this.authChainId || this.labelChainId; }
    get atomName() { return this.authAtomName || this.labelAtomName; }
}

/**
 * Process a CIF loop entry into an array of objects.
 * First process the fields, then the data lines.
 * @param {string} loopKey
 * @param {string} loopContent
 * @returns {{ results: object[], parseErrors: string[] }}
 */
export function cifGetLoop(loopKey, loopContent) {
    const loopLines = loopContent.split('\n').map((line) => line.trim()).filter(Boolean);
    const fields = [];
    const results = [];
    const parseErrors = [];
    let inData = false;
    for (const line of loopLines) {
        if (line.trim().length === 0 || line.trim() === '#') break; // done with loop

        if (!inData) {
            const match = line.match(`^${loopKey}\\.(\\w+)`);
            if (match) {
                const field = match[1];
                fields.push(field);
            } else {
                inData = true;
            }
        }

        if (inData) {
            const values = cifTokenizeLine(line);
            if (values.length === fields.length) {
                const entry = {};
                for (let i = 0; i < fields.length; i++) {
                    entry[fields[i]] = values[i];
                }
                results.push(entry);
            } else {
                parseErrors.push(`Wrong number of values for a loop line: ${values.length} != ${fields.length}:\n${line} != ${fields}`);
            }
        }
    }
    return { results, parseErrors };
}

/**
 * Tokenize a CIF loop line into an array of tokens.
 * The tokens are separated by whitespace, with optional quoting to embed whitespace within tokens.
 * @param {string} cifString
 * @returns {string[]}
 */
export function cifTokenizeLine(cifString) {
    const tokens = [];
    let inToken = null;
    let inQuoteSep = null;
    function finishToken() {
        tokens.push(inToken);
        inToken = null;
        inQuoteSep = null;
    }
    for (let i = 0; i < cifString.length; i++) {
        const char = cifString[i];
        if (char.match(/\s/)) {
            if (inQuoteSep) inToken += char;
            else if (inToken != null) finishToken();
            // Nothing to do otherwise
            continue;
        } else if (char.match(/['"]/)) {
            if (inQuoteSep) {
                if (inQuoteSep === char) finishToken();
                else inToken += char;
            } else if (inToken != null) {
                inToken += char;
            } else {
                inQuoteSep = char;
                inToken = '';
            }
        } else {
            if (inToken == null) inToken = '';
            inToken += char;
        }
    }
    if (inToken != null) finishToken();
    return tokens;
}

/**
 * Collect a list of CifAtomRaw into CifResidues.
 * @param {CifAtomRaw[]} cifRawAtoms
 * @returns {CifResidue[]}
 */
export function collectResiduesFromCifAtoms(cifRawAtoms) {
    const residues = [];
    let workingResidue;
    function finishResidue() {
        if (workingResidue) {
            residues.push(workingResidue);
            workingResidue = null;
        }
    }
    for (const atom of cifRawAtoms) {
        const resKey = atom.resKey;
        if (!workingResidue || workingResidue.resKey !== resKey) {
            finishResidue();
            workingResidue = new CifResidue(atom);
        } else {
            workingResidue.addAtom(atom);
        }
    }
    finishResidue();
    return residues;
}
